import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, type TextareaHTMLAttributes } from "react";

type Props = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /** Tallest the box grows (CSS px, before the app zoom) before it scrolls.
   *  The default holds ~10 lines of chat text. */
  maxHeight?: number;
};

/**
 * A textarea that grows with its text, so a long message can be read whole
 * while it's written instead of one scrolled line at a time (support ticket
 * TKT-00158). Sized from the value on every change, so it also shrinks back to
 * one line when the parent clears it after sending — the old onChange-only
 * resize left the box tall and empty.
 */
export const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, Props>(function AutoGrowTextarea(
  { maxHeight = 260, rows = 1, style, value, ...rest },
  ref,
) {
  const inner = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLTextAreaElement);

  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    el.style.height = "auto";
    // border-box: scrollHeight covers content + padding, the borders come on top.
    const cs = window.getComputedStyle(el);
    const borders = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const full = el.scrollHeight + borders;
    el.style.height = `${Math.min(full, maxHeight)}px`;
    el.style.overflowY = full > maxHeight ? "auto" : "hidden";
  }, [value, maxHeight]);

  return <textarea ref={inner} rows={rows} value={value} style={{ ...style, resize: "none" }} {...rest} />;
});
